import type { ParseKeys, TOptions } from 'i18next';

import type { PORTAL_UI_NAMESPACE } from '../../i18n/portalUiI18n';

export type ApplicationSwitcherKey = ParseKeys<
  typeof PORTAL_UI_NAMESPACE,
  TOptions,
  'applicationSwitcher'
>;

export type ApplicationId =
  'portal' | 'portal-configurator' | 'user-management' | 'trace';

export const SCOPE_ADMIN_PORTAL = 'admin.portal';
export const SCOPE_USER_MANAGEMENT_READ = 'admin.usermanagement:read';
export const SCOPE_USER_MANAGEMENT = 'admin.usermanagement';
export const SCOPE_APP_TRACE = 'app.trace';

export type ApplicationIconModifier =
  'portal' | 'configurator' | 'user-management' | 'trace';

export type ApplicationDefinition = {
  id: ApplicationId;
  titleKey: ApplicationSwitcherKey;
  descriptionKey: ApplicationSwitcherKey;
  iconName: string;
  iconModifier: ApplicationIconModifier;
  path: string;
  isVisible: (scopes: string[]) => boolean;
};

function hasUserManagementAccess(scopes: string[]): boolean {
  return (
    scopes.includes(SCOPE_USER_MANAGEMENT_READ) ||
    scopes.includes(SCOPE_USER_MANAGEMENT)
  );
}

export const APPLICATIONS: ApplicationDefinition[] = [
  {
    id: 'portal',
    titleKey: 'applications.portal.title',
    descriptionKey: 'applications.portal.description',
    iconName: 'dashboard',
    iconModifier: 'portal',
    path: '/',
    isVisible: () => true,
  },
  {
    id: 'portal-configurator',
    titleKey: 'applications.portalConfigurator.title',
    descriptionKey: 'applications.portalConfigurator.description',
    iconName: 'dashboard_customize',
    iconModifier: 'configurator',
    path: '/poco/',
    isVisible: (scopes) => scopes.includes(SCOPE_ADMIN_PORTAL),
  },
  {
    id: 'user-management',
    titleKey: 'applications.userManagement.title',
    descriptionKey: 'applications.userManagement.description',
    iconName: 'manage_accounts',
    iconModifier: 'user-management',
    path: '/user-management/',
    isVisible: hasUserManagementAccess,
  },
  {
    id: 'trace',
    titleKey: 'applications.trace.title',
    descriptionKey: 'applications.trace.description',
    iconName: 'document_scanner',
    iconModifier: 'trace',
    path: '/trace/',
    isVisible: (scopes) => scopes.includes(SCOPE_APP_TRACE),
  },
];

export function getVisibleApplications(
  scopes: string[]
): ApplicationDefinition[] {
  return APPLICATIONS.filter((application) => application.isVisible(scopes));
}

export function detectCurrentApplicationId(
  pathname: string = globalThis.location?.pathname ?? '/'
): ApplicationId {
  if (pathname.startsWith('/user-management')) {
    return 'user-management';
  }

  if (pathname.startsWith('/poco')) {
    return 'portal-configurator';
  }

  if (pathname.startsWith('/trace')) {
    return 'trace';
  }

  return 'portal';
}

export function buildApplicationUrl(path: string): string {
  return new URL(path, globalThis.location.origin).href;
}

export function hasAlternativeApplications(
  scopes: string[],
  currentAppId: ApplicationId
): boolean {
  return getVisibleApplications(scopes).some(
    (application) => application.id !== currentAppId
  );
}
